import styled from 'styled-components';
import { CashFlowMetricsDto } from 'api/models';
import { formatCurrency } from 'utility/utility';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useTranslation } from 'react-i18next';

const Container = styled.div`
  background: white;
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h3`
  font-size: 18px;
  font-weight: bold;
  color: #1f2937;
  margin-bottom: 24px;
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 24px;
`;

const MetricCard = styled.div<{ variant: 'blue' | 'green' | 'red' }>`
  padding: 16px;
  border-radius: 8px;
  background: ${props => {
    switch (props.variant) {
      case 'blue':
        return '#EBF5FF';
      case 'green':
        return '#ECFDF5';
      case 'red':
        return '#FEF2F2';
      default:
        return '#EBF5FF';
    }
  }};
`;

const CardLabel = styled.p`
  font-size: 14px;
  color: #4b5563;
`;

const CardValue = styled.p<{ variant: 'blue' | 'green' | 'red' }>`
  font-size: 18px;
  font-weight: bold;
  color: ${props => {
    switch (props.variant) {
      case 'blue':
        return '#3B82F6';
      case 'green':
        return '#10B981';
      case 'red':
        return '#EF4444';
      default:
        return '#3B82F6';
    }
  }};
  margin-top: 4px;
`;

const SubValue = styled.p`
  font-size: 12px;
  color: #6b7280;
  margin-top: 4px;
`;

const ChartContainer = styled.div`
  height: 256px;
`;

export const CashFlowMetrics = ({ data }: { data: CashFlowMetricsDto }) => {
  const { t } = useTranslation();

  const chartData = [
    {
      name: t('cash-flow-metrics:total-due', 'Total Due'),
      value: data.totalDue || 0,
    },
    {
      name: t('cash-flow-metrics:received', 'Received'),
      value: data.totalReceived || 0,
    },
    {
      name: t('cash-flow-metrics:overdue', 'Overdue'),
      value: data.totalOverdue || 0,
    },
  ];

  return (
    <Container>
      <Title>{t('cash-flow-metrics:title', 'Cash Flow Overview')}</Title>

      <CardsGrid>
        <MetricCard variant="blue">
          <CardLabel>{t('cash-flow-metrics:total-due', 'Total Due')}</CardLabel>
          <CardValue variant="blue">{formatCurrency(data.totalDue || 0)}</CardValue>
          <SubValue>
            {t('cash-flow-metrics:next-month-due', 'Next Month')}: {formatCurrency(data.nextMonthDue || 0)}
          </SubValue>
        </MetricCard>

        <MetricCard variant="green">
          <CardLabel>{t('cash-flow-metrics:received', 'Received')}</CardLabel>
          <CardValue variant="green">{formatCurrency(data.totalReceived || 0)}</CardValue>
        </MetricCard>

        <MetricCard variant="red">
          <CardLabel>{t('cash-flow-metrics:overdue', 'Overdue')}</CardLabel>
          <CardValue variant="red">{formatCurrency(data.totalOverdue || 0)}</CardValue>
          <SubValue>
            {t('cash-flow-metrics:overdue-payments-count', '{{count}} payments', {
              count: data.overduePaymentsCount || 0,
            })}
          </SubValue>
        </MetricCard>
      </CardsGrid>

      <ChartContainer>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="name" tick={{ fill: '#6B7280' }} />
            <YAxis tick={{ fill: '#6B7280' }} />
            <Tooltip
              formatter={value => formatCurrency(Number(value))}
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #E5E7EB',
                borderRadius: '6px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              }}
            />
            <Bar dataKey="value" fill="#3B82F6" radius={[4, 4, 0, 0]} maxBarSize={60} />
          </BarChart>
        </ResponsiveContainer>
      </ChartContainer>
    </Container>
  );
};